import {
  createColumn,
  createColumnsBulk,
  deleteColumn as deleteColumnAPI,
  updateColumn,
  updateColumnOrder,
  updateColumnValue as updateColumnValueAPI,
} from '@api/customColumns.js';
import toast from '@components/Common/Toast.js';
import { COLUMN_EVENT_TYPES, columnUpdate } from '@signals/project/index.js';
import ProjectsState from '@states/Projects.js';

export const defaultColumns = [
  {
    key: 'select',
    name: 'Status',
    icon: 'square-menu',
    bgColor: 'bg-green-300',
    textColor: 'text-green-800',
    defaultTitle: 'Status',
    defaultOptions: {
      'in-backlog': {
        color: '#007bff',
        label: 'In Backlog',
        order: 0,
        isDeactivated: false,
      },
      'in-progress': {
        color: '#ffa500',
        label: 'In Progress',
        order: 1,
        isDeactivated: false,
      },
      'in-review': {
        color: '#6f42c1',
        label: 'In Review',
        order: 2,
        isDeactivated: false,
      },
      completed: {
        color: '#28a745',
        label: 'Completed',
        order: 3,
        isDeactivated: false,
      },
      'on-hold': {
        color: '#6c757d',
        label: 'On Hold',
        order: 4,
        isDeactivated: false,
      },
    },
  },
  {
    key: 'text',
    name: 'Text',
    icon: 'type',
    bgColor: 'bg-yellow-300',
    textColor: 'text-yellow-800',
    defaultTitle: 'Text',
  },
  {
    key: 'people',
    name: 'People',
    icon: 'user',
    bgColor: 'bg-blue-300',
    textColor: 'text-blue-800',
    defaultTitle: 'People',
  },
  {
    key: 'date',
    name: 'Date',
    icon: 'calendar',
    bgColor: 'bg-purple-300',
    textColor: 'text-purple-800',
    defaultTitle: 'Date',
  },
  {
    key: 'number',
    name: 'Numbers',
    icon: 'hash',
    bgColor: 'bg-yellow-300',
    textColor: 'text-yellow-800',
    defaultTitle: 'Numbers',
  },
  {
    key: 'checkbox',
    name: 'Checkbox',
    icon: 'check',
    bgColor: 'bg-red-300',
    textColor: 'text-gray-800',
    defaultTitle: 'Checkbox',
  },
  {
    key: 'link',
    name: 'Link',
    icon: 'link',
    bgColor: 'bg-indigo-300',
    textColor: 'text-indigo-800',
    defaultTitle: 'Link',
  },
];

const getDefaultWidthByType = type => {
  switch (type) {
    case 'select':
      return 135;
    case 'text':
      return 150;
    case 'people':
      return 120;
    case 'date':
      return 125;
    case 'number':
      return 120;
    case 'checkbox':
      return 100;
    case 'link':
      return 200;
    default:
      return 150;
  }
};

/**
 * Add a new column
 */
export async function addColumn(column, currentFolderId = null) {
  if (!column.key || !column.title || !column.type) {
    throw new Error('Column must have key, title, and type');
  }

  // Get the current folder columns
  const columns = getFolderColumns(currentFolderId);

  if (columns.some(c => c.key === column.key)) {
    throw new Error(`Column with key ${column.key} already exists`);
  }

  // Define valid column types (similar to defaultColumns)
  const validColumnTypes = [
    'select',
    'text',
    'people',
    'date',
    'number',
    'checkbox',
    'link',
  ];
  if (!validColumnTypes.includes(column.type)) {
    throw new Error(`Invalid column type: ${column.type}`);
  }

  try {
    // Get organization and project info from ProjectsState
    const { rootFolderOrganizationId } = ProjectsState.projects;
    if (!rootFolderOrganizationId && column.type === 'people') {
      throw new Error('Cannot create a "People" column in personal projects.');
    }
    const organizationId = rootFolderOrganizationId;
    const projectId = currentFolderId; // Use currentFolderId as projectId, null for root

    const maxOrder = Math.max(0, ...columns.map(c => c.order || 0));

    const response = await createColumn({
      name: column.title,
      type: column.type,
      options: column.options,
      organizationId,
      projectId,
      order: maxOrder + 1,
      width: parseInt(column.width) || getDefaultWidthByType(column.type),
      hidden: false,
      isCustom: true,
    });

    const savedColumn = response.response;
    if (!savedColumn || !savedColumn.id) {
      throw new Error('Failed to create column: no ID returned from server');
    }

    const newColumn = {
      key: (savedColumn.name ? savedColumn.name : column.title)
        .toLowerCase()
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
          index === 0 ? word.toLowerCase() : word.toUpperCase(),
        )
        .replace(/\s+/g, ''),
      title: savedColumn.name || column.title,
      type: savedColumn.type,
      isVisible: true,
      width: savedColumn.width + 'px',
      options: savedColumn.options,
      id: savedColumn.id,
      order: savedColumn.order,
      isCustom: true,
      draggable: true,
      projectId: savedColumn.projectId,
    };

    // Update ProjectsState with the new column
    const currentState = ProjectsState.projects;

    if (currentFolderId === null) {
      // Update root folder columns
      const updatedRootFolderInfo = {
        ...currentState.rootFolderInfo,
        customColumns: [
          ...currentState.rootFolderInfo.customColumns,
          newColumn,
        ].sort((a, b) => a.order - b.order),
      };

      ProjectsState.setProjects({
        ...currentState,
        rootFolderInfo: updatedRootFolderInfo,
      });
    } else {
      // Update specific folder columns
      const updatedFolders = currentState.folders.map(folder => {
        if (folder.id === currentFolderId) {
          return {
            ...folder,
            customColumns: [...(folder.customColumns || []), newColumn].sort(
              (a, b) => a.order - b.order,
            ),
          };
        }
        return folder;
      });

      ProjectsState.setProjects({
        ...currentState,
        folders: updatedFolders,
      });
    }

    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.ADDED,
      columnId: newColumn.id,
      currentFolderId,
      column: { ...newColumn },
    });

    return newColumn;
  } catch (error) {
    console.error('Failed to add column:', error);
    throw error;
  }
}

/**
 * Add multiple columns in bulk
 */
export async function addColumns(columns, currentFolderId = null) {
  if (!Array.isArray(columns) || columns.length === 0) {
    throw new Error('Columns must be a non-empty array');
  }

  // Validate all columns first
  for (const column of columns) {
    if (!column.name || !column.type) {
      throw new Error('Each column must have name and type');
    }
  }

  // Get current columns to check for duplicates and calculate order
  const existingColumns = getFolderColumns(currentFolderId);

  // Check for duplicate names
  for (const column of columns) {
    const existingColumn = existingColumns.find(
      c => c.title.toLowerCase() === column.name.toLowerCase(),
    );
    if (existingColumn) {
      throw new Error(`Column with name "${column.name}" already exists`);
    }
  }

  // Check for duplicates within the new columns
  const columnNames = columns.map(c => c.name.toLowerCase());
  const duplicates = columnNames.filter(
    (name, index) => columnNames.indexOf(name) !== index,
  );
  if (duplicates.length > 0) {
    throw new Error(
      `Duplicate column names in bulk creation: ${[...new Set(duplicates)].join(', ')}`,
    );
  }

  // Define valid column types
  const validColumnTypes = [
    'select',
    'text',
    'people',
    'date',
    'number',
    'checkbox',
    'link',
  ];

  for (const column of columns) {
    if (!validColumnTypes.includes(column.type)) {
      throw new Error(`Invalid column type: ${column.type}`);
    }
  }

  try {
    // Get organization and project info from ProjectsState
    const { rootFolderOrganizationId } = ProjectsState.projects;

    // Check people column restriction for personal projects
    const hasPeopleColumns = columns.some(col => col.type === 'people');
    if (!rootFolderOrganizationId && hasPeopleColumns) {
      throw new Error('Cannot create "People" columns in personal projects.');
    }

    const res = await createColumnsBulk(columns);
    const savedColumns = res.response;

    if (!savedColumns || !Array.isArray(savedColumns)) {
      throw new Error('Failed to create columns: invalid response from server');
    }

    // Transform saved columns to the expected format
    const newColumns = savedColumns.map(savedColumn => ({
      key: (savedColumn.name ? savedColumn.name : savedColumn.title)
        .toLowerCase()
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
          index === 0 ? word.toLowerCase() : word.toUpperCase(),
        )
        .replace(/\s+/g, ''),
      title: savedColumn.name || savedColumn.title,
      type: savedColumn.type,
      isVisible: true,
      width: savedColumn.width + 'px',
      options: savedColumn.options,
      id: savedColumn.id,
      order: savedColumn.order,
      isCustom: true,
      draggable: true,
      projectId: savedColumn.projectId,
    }));

    // Update ProjectsState with the new columns
    const currentState = ProjectsState.projects;

    if (currentFolderId === null) {
      // Update root folder columns
      const updatedRootFolderInfo = {
        ...currentState.rootFolderInfo,
        customColumns: [
          ...currentState.rootFolderInfo.customColumns,
          ...newColumns,
        ].sort((a, b) => a.order - b.order),
      };

      ProjectsState.setProjects({
        ...currentState,
        rootFolderInfo: updatedRootFolderInfo,
      });
    } else {
      // Update specific folder columns
      const updatedFolders = currentState.folders.map(folder => {
        if (folder.id === currentFolderId) {
          return {
            ...folder,
            customColumns: [
              ...(folder.customColumns || []),
              ...newColumns,
            ].sort((a, b) => a.order - b.order),
          };
        }
        return folder;
      });

      ProjectsState.setProjects({
        ...currentState,
        folders: updatedFolders,
      });
    }

    // Dispatch events for each new column
    newColumns.forEach(newColumn => {
      columnUpdate.dispatch({
        type: COLUMN_EVENT_TYPES.ADDED,
        columnId: newColumn.id,
        currentFolderId,
        column: { ...newColumn },
      });
    });

    return newColumns;
  } catch (error) {
    console.error('Failed to add columns in bulk:', error);
    throw error;
  }
}

/**
 * Delete a column
 */
export async function deleteColumn(currentFolderId, columnId) {
  const columns = getFolderColumns(currentFolderId);
  const column = columns.find(col => col.id === columnId);
  if (!column) {
    throw new Error(`Column with ID ${columnId} not found`);
  }

  if (column.isFixed) {
    throw new Error('Cannot delete fixed columns');
  }

  try {
    // Store column data before deletion for the event
    const columnData = { ...column };

    // Call the API to delete the column
    await deleteColumnAPI(columnId);

    // Update the local state by removing the column
    const columnIndex = columns.findIndex(col => col.id === columnId);
    if (columnIndex > -1) {
      columns.splice(columnIndex, 1);
    }

    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.DELETED,
      columnId,
      currentFolderId,
      column: columnData, // Pass the column data before deletion
    });

    return true;
  } catch (error) {
    console.error('Failed to delete column:', error);
    throw error;
  }
}

/**
 * Set column visibility
 */
export async function setColumnVisibility(
  currentFolderId,
  columnId,
  isVisible,
  // I added this parameter to allow user to still update the column visibility but we will only save it (API call) if canUpdate is true
  canUpdate = true,
) {
  const columns = getFolderColumns(currentFolderId);
  const column = columns.find(col => col.id === columnId);
  if (!column) {
    throw new Error(`Column with ID ${columnId} not found`);
  }

  if (column.isFixed && !isVisible) {
    throw new Error('Cannot hide fixed columns');
  }

  try {
    // Call the API to update the column visibility
    if (canUpdate) {
      await updateColumn(columnId, {
        hidden: !isVisible,
      });
    }

    // Update the local state
    column.isVisible = isVisible;

    // Trigger column visibility change event
    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.VISIBILITY_CHANGED,
      columnId,
      isVisible,
      currentFolderId,
      column: { ...column }, // Pass a copy of the column object
    });

    return column;
  } catch (error) {
    console.error('Failed to update column visibility:', error);
    throw error;
  }
}

/**
 * Update column value for an entity
 */
export async function updateColumnValue(
  columnId,
  entityType,
  entityId,
  value,
  rename = false,
) {
  try {
    // Call the API to update the column value
    await updateColumnValueAPI(columnId, entityType, entityId, value, rename);

    // Update projects state
    const updateItem = items => {
      return items.map(item => {
        if (item.id === entityId) {
          const newColumnValues = Array.isArray(item.columnValues)
            ? [...item.columnValues]
            : [];
          const index = newColumnValues.findIndex(
            cv => cv.columnId === columnId,
          );
          if (index >= 0) {
            newColumnValues[index] = { ...newColumnValues[index], value };
          } else {
            newColumnValues.push({ columnId, value });
          }
          // if we changed the name of the column also change the name key
          if (rename) {
            return { ...item, columnValues: newColumnValues, name: value };
          }
          return { ...item, columnValues: newColumnValues };
        }
        return item;
      });
    };

    const { folders, tasks } = ProjectsState.projects;
    const newFolders = updateItem(folders);
    const newTasks = updateItem(tasks);
    ProjectsState.setProjects({
      ...ProjectsState.projects,
      folders: newFolders,
      tasks: newTasks,
    });

    return true;
  } catch (error) {
    toast.error(i18next.t('pages.projects.toast.failed_column_update'));
    console.error('Failed to update column value:', error);
    throw error;
  }
}

/**
 * Get folder columns
 */
export function getFolderColumns(currentFolderId = null) {
  let columns;
  // if null we get the root folder columns otherwise we get the columns for the current folder
  if (currentFolderId) {
    const folder = ProjectsState.projects.folders.find(
      f => f.id === currentFolderId,
    );
    if (folder && folder.customColumns) {
      columns = folder.customColumns;
    } else {
      columns = [];
    }
  } else {
    // Return columns from root folder
    columns = ProjectsState.projects.rootFolderInfo?.customColumns || [];
  }

  // Sort columns by order property (if it exists), otherwise maintain original order
  return columns.sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : 999;
    const orderB = typeof b.order === 'number' ? b.order : 999;
    return orderA - orderB;
  });
}

/**
 * Rename a column
 */
export async function renameColumn(currentFolderId, columnId, newTitle) {
  const columns = getFolderColumns(currentFolderId);
  const column = columns.find(col => col.id === columnId);
  if (!column) {
    throw new Error(`Column with ID ${columnId} not found`);
  }

  const oldTitle = column.title; // Store old title before changes

  try {
    // Call the API to update the column
    await updateColumn(columnId, {
      name: newTitle,
    });

    // Update the local state
    column.title = newTitle;

    // Trigger column title change event
    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.TITLE_CHANGED,
      columnId,
      oldTitle,
      newTitle,
      currentFolderId,
      column: { ...column }, // Pass a copy of the column object
    });

    return column;
  } catch (error) {
    console.error('Failed to update column title:', error);
    throw error;
  }
}

/**
 * Rename a column
 */
export async function orderColumn(currentFolderId, newOrder) {
  const columns = getFolderColumns(currentFolderId);
  if (!columns) throw new Error('Columns not found');

  try {
    // Call the API to update the column
    await updateColumnOrder({
      columnOrder: newOrder,
    });

    // re‑assign each column.order based on newOrder’s index
    columns.forEach(column => {
      const idx = newOrder.findIndex(c => c.id === column.id);
      column.order = idx >= 0 ? idx : 999;
    });

    // Trigger column ordering event
    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.ORDERING,
      currentFolderId,
    });
  } catch (error) {
    console.error('Failed to update column order:', error);
    throw error;
  }
}

/**
 * Resize a column
 */
export async function resizeColumn(currentFolderId, columnId, newWidth) {
  const columns = getFolderColumns(currentFolderId);
  const column = columns.find(col => col.id === columnId);
  if (!column) {
    throw new Error(`Column with ID ${columnId} not found`);
  }

  const oldWidth = column.width; // Store old width before changes

  try {
    // Call the API to update the column
    await updateColumn(columnId, {
      width: newWidth,
    });

    // Update the local state
    column.width = `${newWidth}px`; // Ensure width is stored with 'px' suffix

    // Trigger column width change event
    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.WIDTH_CHANGED,
      columnId,
      oldWidth,
      newWidth,
      currentFolderId,
      column: { ...column }, // Pass a copy of the column object
    });

    return column;
  } catch (error) {
    console.error('Failed to update column width:', error);
    throw error;
  }
}

/**
 * Sort a column
 */
export async function sortColumn(currentFolderId, columnId, sortDirection) {
  const columns = getFolderColumns(currentFolderId);
  const column = columns.find(col => col.id === columnId);
  if (!column) {
    throw new Error(`Column with ID ${columnId} not found`);
  }

  // avoid sorting if the column is already sorted in the same direction
  if (column.sortingBy === sortDirection) return column;

  try {
    // Clear sorting from all other columns first
    const columnsToUpdate = columns.filter(
      c => c.id !== columnId && c.id && c.sortingBy,
    );

    await Promise.all(
      columnsToUpdate.map(c =>
        updateColumn(c.id, {
          sortingBy: null,
        }),
      ),
    );

    // Update columns in local state - clear sorting from all other columns
    columns.forEach(c => {
      if (c.id !== columnId) {
        c.sortingBy = null;
      }
    });

    // Call the API to update the column sort
    await updateColumn(columnId, {
      sortingBy: sortDirection,
    });

    // Update the local state
    column.sortingBy = sortDirection;

    // Reset rowOrder values for all items in the current folder since column sorting overrides manual order
    const currentState = ProjectsState.projects;

    if (currentFolderId === null) {
      // Reset rowOrder for all root-level folders
      const updatedFolders = currentState.folders.map(folder => ({
        ...folder,
        rowOrder: null,
      }));

      ProjectsState.setProjects({
        ...currentState,
        folders: updatedFolders,
      });
    } else {
      // Reset rowOrder for all tasks in the current folder
      const updatedTasks = currentState.tasks.map(task => {
        if (task.parentId === currentFolderId) {
          return {
            ...task,
            rowOrder: null,
          };
        }
        return task;
      });

      ProjectsState.setProjects({
        ...currentState,
        tasks: updatedTasks,
      });
    }

    // Trigger column sorting change event
    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.SORTED,
      columnId,
      sortDirection,
      currentFolderId,
      column: { ...column }, // Pass a copy of the column object
    });

    return column;
  } catch (error) {
    console.error('Failed to update column sorting:', error);
    throw error;
  }
}

/**
 * Get visible columns
 */
export function getVisibleColumns(currentFolderId = null) {
  let columns;
  // if null we get the root folder columns otherwise we get the columns for the current folder
  if (currentFolderId) {
    const folder = ProjectsState.projects.folders.find(
      f => f.id === currentFolderId,
    );
    if (folder && folder.customColumns) {
      columns = folder.customColumns.filter(col => col.isVisible);
    } else {
      columns = [];
    }
  } else {
    // Return visible columns from root folder
    columns = (
      ProjectsState.projects.rootFolderInfo?.customColumns || []
    ).filter(col => col.isVisible);
  }

  // Sort columns by order property (if it exists), otherwise maintain original order
  return columns.sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : 999;
    const orderB = typeof b.order === 'number' ? b.order : 999;
    return orderA - orderB;
  });
}

//   // ============ SORTING UTILITIES ============

/**
 * Advanced sort function that handles column types and custom column values
 * Priority: rowOrder values take precedence over column sorting
 * @param {Array} items - Array of items to sort
 * @param {string} columnKey - Column key to sort by
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @param {string} currentFolderId - Current folder ID to get column definitions
 * @returns {Array} Sorted array
 */
export function sortItems(items, columnKey, direction, currentFolderId = null) {
  const columns = getFolderColumns(currentFolderId);
  const columnDefinition = columns.find(
    col => col.key === columnKey || col.id === columnKey,
  );
  const columnType = columnDefinition?.type;

  // Column-based sorting
  const columnsSortedItems = [...items].sort((a, b) => {
    let valueA, valueB;

    // Handle standard item properties
    if (['name', 'createdAt', 'updatedAt'].includes(columnKey)) {
      valueA = a[columnKey];
      valueB = b[columnKey];
    } else if (columnKey === 'assignees') {
      valueA = Array.isArray(a.assignees) ? a.assignees.length : 0;
      valueB = Array.isArray(b.assignees) ? b.assignees.length : 0;
    } else if (
      a.hasOwnProperty(columnKey) &&
      b.hasOwnProperty(columnKey) &&
      !columnIdNumber
    ) {
      valueA = a[columnKey];
      valueB = b[columnKey];
    } else if (columnDefinition?.id && a.columnValues && b.columnValues) {
      // Handle custom column values
      const colValA = a.columnValues.find(
        cv => cv.columnId === columnDefinition.id,
      );
      const colValB = b.columnValues.find(
        cv => cv.columnId === columnDefinition.id,
      );
      valueA = colValA?.value;
      valueB = colValB?.value;
    } else {
      valueA = a[columnKey];
      valueB = b[columnKey];
    }

    const isEmpty = v => v === null || v === undefined || v === '';
    const aIsEmpty = isEmpty(valueA);
    const bIsEmpty = isEmpty(valueB);

    if (aIsEmpty && !bIsEmpty) return direction === 'asc' ? 1 : -1;
    if (!aIsEmpty && bIsEmpty) return direction === 'asc' ? -1 : 1;
    if (aIsEmpty && bIsEmpty) return 0;

    let comparison = 0;

    // Type-specific sorting
    if (
      columnType === 'date' ||
      (typeof valueA === 'string' &&
        typeof valueB === 'string' &&
        (/^\d{4}-\d{2}-\d{2}(T|\s)?\d{2}:\d{2}:\d{2}/.test(valueA) ||
          /^\d{4}-\d{2}-\d{2}$/.test(valueA)))
    ) {
      const dateA = new Date(valueA);
      const dateB = new Date(valueB);
      if (!isNaN(dateA) && !isNaN(dateB)) {
        comparison = dateA.getTime() - dateB.getTime();
      } else if (!isNaN(dateA)) {
        comparison = -1;
      } else if (!isNaN(dateB)) {
        comparison = 1;
      }
    } else if (
      columnType === 'number' ||
      (!isNaN(Number(valueA)) && !isNaN(Number(valueB)))
    ) {
      comparison = Number(valueA) - Number(valueB);
    } else if (
      columnType === 'checkbox' ||
      (typeof valueA === 'boolean' && typeof valueB === 'boolean')
    ) {
      comparison = !!valueA - !!valueB;
    } else {
      // Default string comparison
      valueA = String(valueA ?? '')
        .toLowerCase()
        .trim();
      valueB = String(valueB ?? '')
        .toLowerCase()
        .trim();
      comparison = valueA.localeCompare(valueB);
    }

    return direction === 'asc' ? comparison : comparison * -1;
  });

  // If rowOrder is defined, use the rowOrder values to move the items position to that index rowOrder
  const finalOrderedItems = sortItemsByRowOrder(columnsSortedItems);
  // If no rowOrder, return the sorted items
  return finalOrderedItems;
}

export function sortItemsByRowOrder(items) {
  const rowOrderItems = items.filter(
    item => item.rowOrder !== null && item.rowOrder !== undefined,
  );
  if (rowOrderItems.length > 0) {
    const sortedItems = [...items];
    rowOrderItems
      .sort((a, b) => a.rowOrder - b.rowOrder) // 1) [ {rowOrder:0}, {rowOrder:1}, {rowOrder:2} ]
      .forEach(item => {
        const from = sortedItems.indexOf(item);
        let to = item.rowOrder; // target zero-based index

        // Ensure target index is within bounds
        to = Math.max(0, Math.min(to, sortedItems.length - 1));

        // 2) remove it
        sortedItems.splice(from, 1);

        // 3) if you pulled it from before your target, everything shifted left,
        //    so you need to move your target one step back:
        if (from < to) to--;

        // 4) put it back at the exact spot
        sortedItems.splice(to, 0, item);
      });
    return sortedItems;
  }
  // If no rowOrder, return the original items
  return items;
}

/**
 * Get the currently sorted column information
 * @param {string} currentFolderId - Current folder ID
 * @returns {Object|null} Object with column key and sort direction, or null if no sorting
 */
export function getCurrentSortInfo(currentFolderId = null) {
  const columns = getFolderColumns(currentFolderId);
  const sortedColumn = columns.find(col => col.sortingBy);

  if (sortedColumn) {
    return {
      columnKey: sortedColumn.key,
      direction: sortedColumn.sortingBy,
      column: sortedColumn,
    };
  }

  return null;
}

const defaultColumnsOrder = [
  { title: 'Name', type: 'text', order: 0 },
  { title: 'Status', type: 'select', order: 1 },
  { title: 'Deadline', type: 'date', order: 3 },
  { title: 'Assignees', type: 'people', order: 4 },
  { title: 'Created At', type: 'date', order: 5 },
];

export async function resetCustomOrder(currentFolderId = null) {
  const columns = getFolderColumns(currentFolderId);
  const createdAtColumn = columns.find(col => col.key === 'createdAt');
  try {
    // grab & re‑order all the built‑ins
    const orderedDefaults = defaultColumnsOrder
      .map((defCol, idx) => {
        const found = columns.find(
          c =>
            !c.isCustom &&
            c.title.toLowerCase() === defCol.title.toLowerCase() &&
            c.type === defCol.type,
        );
        // if you might be missing some defaults, guard against `found` being undefined:
        if (!found) return null;
        return { ...found, order: idx };
      })
      .filter(Boolean);

    // grab the customs, in their current order
    const customCols = columns
      .filter(c => c.isCustom)
      .sort((a, b) => a.order - b.order);

    // renumber the customs starting where defaults end
    const orderedCustoms = customCols.map((col, i) => ({
      ...col,
      order: orderedDefaults.length + i,
    }));

    // final combined order
    const resetSortedColumns = [...orderedDefaults, ...orderedCustoms];

    // Batch update all columns via API to reset width and sorting
    const updatePromises = resetSortedColumns.map(col => {
      const updates = {
        width: 150,
        sortingBy: col.id === createdAtColumn.id ? 'asc' : null,
        order: col.order,
        isVisible: true,
      };
      return updateColumn(col.id, updates);
    });
    await Promise.all(updatePromises);

    const updateColumns = resetSortedColumns.map(col => ({
      ...col,
      width: '150px',
      sortingBy: col.key === 'createdAt' ? 'asc' : null,
      isVisible: true,
    }));

    const currentState = ProjectsState.projects;
    let updatedFolders = [...currentState.folders];
    const updatedRootFolderInfo = { ...currentState.rootFolderInfo };

    if (currentFolderId === null) {
      updatedRootFolderInfo.customColumns = updateColumns;
    } else {
      updatedFolders = updatedFolders.map(folder => {
        if (folder.id === currentFolderId) {
          return {
            ...folder,
            customColumns: updateColumns,
          };
        }
        return folder;
      });
    }

    // Set the new state
    ProjectsState.setProjects({
      ...currentState,
      rootFolderInfo: updatedRootFolderInfo,
      folders: updatedFolders,
    });

    columnUpdate.dispatch({
      type: COLUMN_EVENT_TYPES.RESET,
      currentFolderId,
    });
  } catch (error) {
    console.error('Error resetting view to default:', error);
  }
}
